import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  HandHeart,
  Sparkles,
  TrendingUp,
  Megaphone,
  Users,
  Check,
  ShieldCheck,
} from "lucide-react";
import Nav from "@/components/sections/Nav";
import Footer from "@/components/sections/Footer";
import { api, formatApiError } from "@/lib/api";

const BENEFITS = [
  {
    icon: TrendingUp,
    title: "Real Revenue",
    body: "Earn a generous referral cut on every customer you send our way. Recurring services compound — and so do your payouts.",
  },
  {
    icon: Megaphone,
    title: "Co-Branded Tools",
    body: "Marketing kits, hand-off codes, social posts, and printables that make sharing Ironclad easy and on-brand.",
  },
  {
    icon: Users,
    title: "Community First",
    body: "You're not a number. You join a tight crew of homeowners, creators, and local businesses building something resilient together.",
  },
  {
    icon: ShieldCheck,
    title: "Ironclad Trust",
    body: "Veteran-owned, 5-star service, transparent pricing. The work backs the pitch — your reputation is safe with us.",
  },
];

const STEPS = [
  { n: "01", title: "Apply", body: "Drop your details below. We review every application personally — no bots, no auto-rejections." },
  { n: "02", title: "Get Approved", body: "If we're a fit, you get your affiliate code, marketing kit, and a one-on-one onboarding call." },
  { n: "03", title: "Grow", body: "Share Ironclad. Watch your community grow stronger — and your earnings with it." },
];

export default function Affiliate() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    website: "",
    audience: "",
    message: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/affiliates/apply", form);
      setDone(true);
      toast.success("You're in. We'll be in touch within 48 hours.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="affiliate-page" className="bg-background text-foreground min-h-screen">
      <Nav />

      <main className="pt-32 md:pt-40">
        {/* HERO */}
        <section
          data-testid="affiliate-hero"
          className="relative overflow-hidden grain"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0a0d0a] to-black pointer-events-none" />
          <div
            className="absolute inset-0 opacity-25 mix-blend-screen pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(ellipse at 25% 35%, rgba(127,176,105,0.4), transparent 60%), radial-gradient(ellipse at 75% 65%, rgba(127,176,105,0.18), transparent 65%)",
            }}
          />

          <div className="container-ic relative py-16 md:py-24">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary mb-10"
              data-testid="affiliate-back"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back home
            </Link>

            <div className="overline mb-4">
              <HandHeart className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
              Become an Ironclad Affiliate
            </div>

            <h1
              className="font-display font-black uppercase leading-[0.9] tracking-tighter
                         text-5xl sm:text-6xl md:text-7xl lg:text-[7rem] text-white"
              data-testid="affiliate-headline"
            >
              <span className="block">Grow your hustle.</span>
              <span className="block">
                <span className="text-primary">Strong.</span> Resilient.
              </span>
              <span className="block">Together.</span>
            </h1>

            <p
              className="mt-8 text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed"
              data-testid="affiliate-subcopy"
            >
              Join the Ironclad community. Whether you're a creator, contractor,
              realtor, or just love what we do — partner with us to help your audience
              build pride of place while you build a healthy side stream of income.
              <span className="text-primary font-semibold"> Stronger together.</span>
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#apply"
                className="btn-primary"
                data-testid="affiliate-cta-apply"
              >
                Apply Now
              </a>
              <a href="#how-it-works" className="btn-outline">
                How It Works
              </a>
            </div>
          </div>
        </section>

        {/* BENEFITS */}
        <section
          data-testid="affiliate-benefits"
          className="py-20 md:py-28 bg-background border-t border-border"
        >
          <div className="container-ic">
            <div className="max-w-3xl mb-14">
              <div className="overline mb-4">
                <Sparkles className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
                Why Ironclad
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
                Built for partners.
                <br />
                <span className="text-primary">Backed by the work.</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
              {BENEFITS.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={b.title}
                    className="bg-background p-7 md:p-8"
                    data-testid={`affiliate-benefit-${i}`}
                  >
                    <div className="w-11 h-11 border border-primary/30 flex items-center justify-center mb-5">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-display text-xl font-bold uppercase tracking-tight">
                      {b.title}
                    </h3>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                      {b.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section
          id="how-it-works"
          data-testid="affiliate-how"
          className="py-20 md:py-28 bg-background border-t border-border"
        >
          <div className="container-ic">
            <div className="max-w-3xl mb-14">
              <div className="overline mb-4">How It Works</div>
              <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
                Three steps. <span className="text-primary">No fluff.</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
              {STEPS.map((s) => (
                <div
                  key={s.n}
                  className="bg-background p-8 md:p-10"
                  data-testid={`affiliate-step-${s.n}`}
                >
                  <div className="font-display text-5xl md:text-6xl font-black text-primary/40 mb-4">
                    {s.n}
                  </div>
                  <h3 className="font-display text-2xl font-bold uppercase tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* APPLY */}
        <section
          id="apply"
          data-testid="affiliate-apply"
          className="py-20 md:py-28 bg-background border-t border-border"
        >
          <div className="container-ic">
            <div className="max-w-3xl mb-10">
              <div className="overline mb-4">Apply</div>
              <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter leading-[0.95]">
                Let's build something
                <br />
                <span className="text-primary">resilient.</span>
              </h2>
              <p className="mt-5 text-muted-foreground text-lg">
                Tell us about you. We read every application personally and reply
                within 48 hours.
              </p>
            </div>

            <div className="border border-border bg-card/30 p-6 md:p-10 max-w-3xl">
              {done ? (
                <div
                  className="border border-primary/40 bg-primary/5 p-8 text-center"
                  data-testid="affiliate-success"
                >
                  <Check className="h-10 w-10 text-primary mx-auto mb-3" />
                  <div className="font-display text-2xl font-bold uppercase tracking-tight">
                    You're in the queue.
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">
                    We'll be in touch within 48 hours. Welcome to the Ironclad
                    community.
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-4" data-testid="affiliate-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Full Name *" testId="aff-name">
                      <input
                        required
                        type="text"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        className="input-aff"
                        data-testid="aff-name"
                      />
                    </Input>
                    <Input label="Email *" testId="aff-email">
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        className="input-aff"
                        data-testid="aff-email"
                      />
                    </Input>
                    <Input label="Phone">
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        className="input-aff"
                        data-testid="aff-phone"
                      />
                    </Input>
                    <Input label="Business / Brand">
                      <input
                        type="text"
                        value={form.business_name}
                        onChange={(e) => set("business_name", e.target.value)}
                        className="input-aff"
                        data-testid="aff-business"
                      />
                    </Input>
                    <Input label="Website / Social">
                      <input
                        type="text"
                        placeholder="instagram.com/..., your site, etc."
                        value={form.website}
                        onChange={(e) => set("website", e.target.value)}
                        className="input-aff"
                        data-testid="aff-website"
                      />
                    </Input>
                    <Input label="Audience (size / niche)">
                      <input
                        type="text"
                        placeholder="e.g. 4k IG · San Antonio homeowners"
                        value={form.audience}
                        onChange={(e) => set("audience", e.target.value)}
                        className="input-aff"
                        data-testid="aff-audience"
                      />
                    </Input>
                  </div>
                  <Input label="Tell us why you'd be a fit">
                    <textarea
                      rows={4}
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      className="input-aff resize-none"
                      placeholder="What you do, how you'd promote Ironclad, anything we should know..."
                      data-testid="aff-message"
                    />
                  </Input>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full disabled:opacity-60"
                    data-testid="affiliate-submit"
                  >
                    {submitting ? "Submitting…" : "Submit Application"}
                  </button>
                  <p className="text-[11px] text-muted-foreground text-center">
                    By applying, you agree to receive a follow-up email from Ironclad.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <style>{`
        .input-aff {
          width: 100%;
          background: hsl(var(--background));
          border: 1px solid hsl(var(--border));
          color: hsl(var(--foreground));
          padding: 12px 14px;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 0.2s;
          font-family: inherit;
        }
        .input-aff:focus { border-color: hsl(var(--primary)); }
      `}</style>
    </div>
  );
}

function Input({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
